import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import partyService from '../../services/partyService';
import masterService from '../../services/masterService';
import profileService from '../../services/profileService';
import packageService from '../../services/packageService';
import MasterPageWrapper from './common/MasterPageWrapper';
import FormSection from './common/FormSection';
import MasterToolbar from './components/MasterToolbar';
import MasterTable from './components/MasterTable';
import StatusBadge from './components/StatusBadge';
import ActionMenu from './components/ActionMenu';
import ConfirmModal from './components/ConfirmModal';
import usePagination from '../../hooks/usePagination';
import './PackageMasterPage.css';
import './RateContractPage.css';

const INITIAL_CONTRACT = {
  contractId: 0,
  contractNumber: '',
  contractName: '',
  partyId: '',
  validFrom: new Date().toISOString().split('T')[0],
  validTo: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
  remarks: '',
  isActive: true
};

export const RateContractPage = ({ showToast: propShowToast }) => {
  // Stable Toast reference to prevent re-render / callback loops
  const [localToast, setLocalToast] = useState(null);
  const showToastRef = useRef(propShowToast);
  useEffect(() => {
    showToastRef.current = propShowToast;
  }, [propShowToast]);

  const triggerToast = useCallback((msg, type = 'info') => {
    if (typeof showToastRef.current === 'function') {
      showToastRef.current(msg, type);
    } else {
      setLocalToast({ message: msg, type });
      setTimeout(() => setLocalToast(null), 4000);
    }
  }, []);
  const [searchParams] = useSearchParams();
  const partyParam = searchParams.get('partyId');

  const [viewMode, setViewMode] = useState('list');
  const [isEditMode, setIsEditMode] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form State
  const [formData, setFormData] = useState(INITIAL_CONTRACT);
  const [errors, setErrors] = useState({});

  // Lookups
  const [parties, setParties] = useState([]);
  const [partiesError, setPartiesError] = useState('');

  // List State
  const [contracts, setContracts] = useState([]);
  const [loadingContracts, setLoadingContracts] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [partyFilter, setPartyFilter] = useState(partyParam || '');
  const [statusFilter, setStatusFilter] = useState('all');

  // Sync URL search param if present
  useEffect(() => {
    if (partyParam !== null && partyParam !== undefined) {
      setPartyFilter(partyParam);
    }
  }, [partyParam]);

  // Centralized Pagination
  const { paginatedItems: pagedContracts, paginationProps: contractPaginationProps } = usePagination({
    items: contracts,
    resetDeps: [searchQuery, partyFilter, statusFilter]
  });

  // Items Drawer / Management State
  const [activeContractForItems, setActiveContractForItems] = useState(null);
  const [contractItems, setContractItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [tariffSearchQuery, setTariffSearchQuery] = useState('');

  // Add Item Row State
  const [newItemType, setNewItemType] = useState('TEST'); // 'TEST' | 'PROFILE' | 'PACKAGE'
  const [newItemId, setNewItemId] = useState('');
  const [newRate, setNewRate] = useState('');
  const [newMrp, setNewMrp] = useState('');
  const [newRemarks, setNewRemarks] = useState('');
  const [addingItem, setAddingItem] = useState(false);

  // Catalogs for Item Selection
  const [allTests, setAllTests] = useState([]);
  const [allProfiles, setAllProfiles] = useState([]);
  const [allPackages, setAllPackages] = useState([]);

  // Confirm Modal
  const [confirmTarget, setConfirmTarget] = useState(null);

  // Load Parties
  const loadParties = useCallback(async () => {
    try {
      setPartiesError('');
      const res = await partyService.getParties({ isActive: true });
      if (res && res.success) {
        setParties(res.data || []);
      } else {
        setPartiesError((res && res.message) || 'The list of clients could not be loaded.');
      }
    } catch (err) {
      console.error('Error loading parties:', err);
      const status = err.response?.status;
      setPartiesError(
        status === 403 ? 'Only an Administrator can pick a client here. Sign in as an Administrator.'
          : status === 401 ? 'Your session has ended. Please sign in again.'
            : (err.response?.data?.message || 'The list of clients could not be loaded. Check that the backend is running.'),
      );
    }
  }, []);

  // Load Catalogs
  const loadCatalogs = useCallback(async () => {
    try {
      const [testsRes, profRes, pkgRes] = await Promise.all([
        masterService.getInvestigations({ isActive: true, pageSize: 1000 }),
        profileService.getProfiles({ isActive: true }),
        packageService.getPackages({ isActive: true })
      ]);

      if (testsRes && testsRes.success) setAllTests(testsRes.data?.items || testsRes.data || []);
      if (profRes && profRes.success) setAllProfiles(profRes.data || []);
      if (pkgRes && pkgRes.success) setAllPackages(pkgRes.data || []);
    } catch (err) {
      console.error('Error loading item catalogs:', err);
    }
  }, []);

  // Load Contracts
  const loadContracts = useCallback(async () => {
    setLoadingContracts(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.isActive = statusFilter === 'active';
      if (searchQuery.trim()) params.search = searchQuery.trim();
      if (partyFilter) params.partyId = parseInt(partyFilter, 10);

      const res = await partyService.getContracts(params);
      if (res && res.success) {
        setContracts(res.data || []);
      }
    } catch (err) {
      console.error('Error loading contracts:', err);
      triggerToast(err.response?.data?.message || 'Failed to load contracts', 'error');
    } finally {
      setLoadingContracts(false);
    }
  }, [searchQuery, partyFilter, statusFilter, triggerToast]);

  useEffect(() => {
    loadParties();
    loadCatalogs();
  }, [loadParties, loadCatalogs]);

  useEffect(() => {
    if (viewMode === 'list') {
      loadContracts();
    }
  }, [viewMode, loadContracts]);

  // Open Create Form
  const handleAddNew = () => {
    setFormData({
      ...INITIAL_CONTRACT,
      partyId: partyFilter || ''
    });
    setErrors({});
    setIsEditMode(false);
    setViewMode('form');
  };

  // Open Edit Form
  const handleEdit = (c) => {
    setIsEditMode(true);
    setFormData({
      contractId: c.contractId,
      contractNumber: c.contractNumber || '',
      contractName: c.contractName || '',
      partyId: c.partyId || '',
      validFrom: c.validFrom ? c.validFrom.split('T')[0] : '',
      validTo: c.validTo ? c.validTo.split('T')[0] : '',
      remarks: c.remarks || '',
      isActive: c.isActive
    });
    setErrors({});
    setViewMode('form');
  };

  // Status Toggle
  const handleToggleStatus = (c) => {
    setConfirmTarget({
      contractId: c.contractId,
      contractName: c.contractName,
      nextStatus: !c.isActive
    });
  };

  const confirmStatusChange = async () => {
    if (!confirmTarget) return;
    try {
      await partyService.updateContractStatus(confirmTarget.contractId, confirmTarget.nextStatus);
      triggerToast(`Contract ${confirmTarget.nextStatus ? 'activated' : 'deactivated'} successfully.`, 'success');
      setConfirmTarget(null);
      loadContracts();
    } catch (err) {
      triggerToast(err.response?.data?.message || 'Failed to update contract status', 'error');
    }
  };

  // Load Contract Items
  const handleOpenItems = async (c) => {
    setActiveContractForItems(c);
    setLoadingItems(true);
    try {
      const res = await partyService.getContractItems(c.contractId);
      if (res && res.success) {
        setContractItems(res.data || []);
      }
    } catch (err) {
      console.error('Error loading contract items:', err);
      triggerToast('Could not load contract tariff items', 'error');
    } finally {
      setLoadingItems(false);
    }
  };

  // Add Item to Contract
  const handleAddItem = async (e) => {
    e.preventDefault();
    if (!newItemId || !newRate) {
      triggerToast('Please select an item and enter contract rate', 'warning');
      return;
    }

    setAddingItem(true);
    try {
      await partyService.addContractItem(activeContractForItems.contractId, {
        itemType: newItemType,
        itemId: parseInt(newItemId, 10),
        contractRate: parseFloat(newRate),
        mrpRate: newMrp ? parseFloat(newMrp) : parseFloat(newRate),
        specialRemarks: newRemarks.trim() || null
      });

      triggerToast('Contract item rate saved.', 'success');
      setNewItemId('');
      setNewRate('');
      setNewMrp('');
      setNewRemarks('');

      // Refresh items
      const res = await partyService.getContractItems(activeContractForItems.contractId);
      if (res && res.success) setContractItems(res.data || []);
      loadContracts();
    } catch (err) {
      console.error('Error adding contract item:', err);
      triggerToast(err.response?.data?.message || 'Failed to save item rate', 'error');
    } finally {
      setAddingItem(false);
    }
  };

  // Remove Item
  const handleRemoveItem = async (itemId) => {
    try {
      await partyService.removeContractItem(itemId);
      triggerToast('Contract item removed.', 'success');
      setContractItems(prev => prev.filter(i => i.id !== itemId));
      loadContracts();
    } catch (err) {
      triggerToast(err.response?.data?.message || 'Failed to remove item', 'error');
    }
  };

  // Items filtered for select box based on newItemType
  const selectableCatalog = useMemo(() => {
    if (newItemType === 'TEST') {
      return allTests.map(t => ({ id: t.investigationId, name: `${t.name} (${t.testCode})`, rate: t.baseRate }));
    } else if (newItemType === 'PROFILE') {
      return allProfiles.map(p => ({ id: p.profileId, name: `${p.profileName} (${p.profileCode})`, rate: 0 }));
    } else {
      return allPackages.map(pkg => ({ id: pkg.packageId, name: `${pkg.packageName} (${pkg.packageCode})`, rate: 0 }));
    }
  }, [newItemType, allTests, allProfiles, allPackages]);

  // Live discount preview calculation
  const liveDiscount = useMemo(() => {
    const cRate = parseFloat(newRate);
    const mrp = parseFloat(newMrp);
    if (!isNaN(cRate) && !isNaN(mrp) && mrp > 0) {
      if (cRate < mrp) {
        const saving = mrp - cRate;
        const pct = ((saving / mrp) * 100).toFixed(1);
        return { saving, pct, isDiscount: true };
      } else if (cRate > mrp) {
        const surcharge = cRate - mrp;
        const pct = ((surcharge / mrp) * 100).toFixed(1);
        return { saving: surcharge, pct, isDiscount: false };
      }
    }
    return null;
  }, [newRate, newMrp]);

  // Overall Contract Tariff Statistics
  const tariffStats = useMemo(() => {
    const totalItems = contractItems.length;
    let totalSavings = 0;
    let mrpSum = 0;

    contractItems.forEach(i => {
      const cRate = parseFloat(i.contractRate) || 0;
      const mrp = parseFloat(i.mrpRate) || cRate;
      mrpSum += mrp;
      if (mrp > cRate) totalSavings += (mrp - cRate);
    });

    const avgDiscountPct = mrpSum > 0 ? ((totalSavings / mrpSum) * 100).toFixed(1) : '0.0';
    return { totalItems, totalSavings, avgDiscountPct };
  }, [contractItems]);

  // In-modal search filter for tariff items
  const filteredContractItems = useMemo(() => {
    if (!tariffSearchQuery.trim()) return contractItems;
    const q = tariffSearchQuery.toLowerCase().trim();
    return contractItems.filter(i =>
      (i.itemName && i.itemName.toLowerCase().includes(q)) ||
      (i.itemCode && i.itemCode.toLowerCase().includes(q)) ||
      (i.departmentName && i.departmentName.toLowerCase().includes(q)) ||
      (i.itemType && i.itemType.toLowerCase().includes(q))
    );
  }, [contractItems, tariffSearchQuery]);

  // Validation
  const validateForm = () => {
    const errs = {};
    if (!formData.contractNumber.trim()) errs.contractNumber = 'Contract number is required';
    if (!formData.contractName.trim()) errs.contractName = 'Contract name is required';
    if (!formData.partyId) errs.partyId = 'Please select a Party / Client';
    if (!formData.validFrom || !formData.validTo) errs.dates = 'Validity dates are required';
    if (formData.validFrom > formData.validTo) errs.dates = 'ValidFrom cannot be after ValidTo';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // Save Contract
  const handleSave = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      triggerToast('Please fix required fields.', 'warning');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        contractNumber: formData.contractNumber.trim().toUpperCase(),
        contractName: formData.packageName || formData.contractName.trim(),
        partyId: parseInt(formData.partyId, 10),
        validFrom: formData.validFrom,
        validTo: formData.validTo,
        remarks: formData.remarks?.trim() || null
      };

      if (isEditMode) {
        await partyService.updateContract(formData.contractId, {
          ...payload,
          isActive: formData.isActive
        });
        triggerToast('Contract updated successfully.', 'success');
      } else {
        const res = await partyService.createContract(payload);
        triggerToast('Rate Contract created successfully.', 'success');
        if (res && res.data) {
          // Open item management directly
          handleOpenItems(res.data);
        }
      }

      setViewMode('list');
      loadContracts();
    } catch (err) {
      console.error('Error saving contract:', err);
      triggerToast(err.response?.data?.message || 'Error saving contract', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Table Columns
  const columns = [
    {
      key: 'contractNumber',
      header: 'Contract No',
      render: (row) => <strong style={{ color: 'var(--primary-600)' }}>{row.contractNumber}</strong>
    },
    {
      key: 'contractName',
      header: 'Contract Description',
      render: (row) => (
        <div>
          <div style={{ fontWeight: 600 }}>{row.contractName}</div>
          <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
            Client: <strong>{row.partyName}</strong> ({row.classificationName})
          </div>
        </div>
      )
    },
    {
      key: 'validity',
      header: 'Validity Period',
      render: (row) => (
        <span style={{ fontSize: '0.82rem' }}>
          {row.validFrom?.split('T')[0]} to {row.validTo?.split('T')[0]}
        </span>
      )
    },
    {
      key: 'itemCount',
      header: 'Contracted Rates',
      render: (row) => (
        <span style={{
          background: row.itemCount > 0 ? '#e0e7ff' : '#f1f5f9',
          color: row.itemCount > 0 ? '#3730a3' : '#64748b',
          padding: '0.15rem 0.55rem',
          borderRadius: '12px',
          fontSize: '0.75rem',
          fontWeight: 600
        }}>
          {row.itemCount || 0} Items
        </span>
      )
    },
    {
      key: 'isActive',
      header: 'Status',
      render: (row) => <StatusBadge isActive={row.isActive} />
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (row) => (
        <ActionMenu
          actions={[
            {
              label: 'Configure Tariff Rates',
              onClick: () => handleOpenItems(row)
            },
            {
              label: 'Edit Contract Details',
              onClick: () => handleEdit(row)
            },
            {
              label: row.isActive ? 'Deactivate' : 'Activate',
              onClick: () => handleToggleStatus(row),
              danger: row.isActive
            }
          ]}
        />
      )
    }
  ];

  return (
    <MasterPageWrapper
      masterTitle="B2B Rate Contract Master"
      masterName="Rate Contract"
      viewMode={viewMode}
      onToggleView={(mode) => {
        if (mode === 'form') handleAddNew();
        else setViewMode('list');
      }}
    >
      {localToast && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          zIndex: 9999,
          padding: '12px 20px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          background: localToast.type === 'error' ? '#ef4444' : localToast.type === 'warning' ? '#f59e0b' : '#10b981',
          color: '#ffffff',
          fontWeight: 600,
          fontSize: '0.875rem'
        }}>
          {localToast.message}
        </div>
      )}
      {viewMode === 'list' ? (
        <>
          <MasterToolbar
            searchPlaceholder="Search Contract Number, Name, Client..."
            searchValue={searchQuery}
            onSearchChange={setSearchQuery}
            filters={[
              {
                id: 'partyFilter',
                label: 'Client / Party',
                value: partyFilter,
                onChange: setPartyFilter,
                options: [
                  { value: '', label: 'All B2B Parties' },
                  ...parties.map(p => ({ value: String(p.partyId), label: `${p.partyName} (${p.partyCode})` }))
                ]
              },
              {
                id: 'statusFilter',
                label: 'Status',
                value: statusFilter,
                onChange: setStatusFilter,
                options: [
                  { value: 'all', label: 'All Statuses' },
                  { value: 'active', label: 'Active Only' },
                  { value: 'inactive', label: 'Inactive Only' }
                ]
              }
            ]}
          />

          <MasterTable
            columns={columns}
            data={pagedContracts}
            loading={loadingContracts}
            emptyTitle="No Rate Contracts Configured"
            emptyDescription="Create contractual price lists for corporate tie-ups and referral hospitals."
            pagination={contractPaginationProps}
          />
        </>
      ) : (
        <form onSubmit={handleSave} className="fade-in rc-form">
          <FormSection
            title="Contract Terms & Identification"
            description="Specify contract identifier, associated B2B client party, and validity date range."
          >
            <div className="form-row">
              <div className="form-group">
                <label className="form-label required">Contract Number / Code</label>
                <input
                  type="text"
                  className={`form-input ${errors.contractNumber ? 'is-invalid' : ''}`}
                  placeholder="e.g. CTR-CORP-2026, HOSP-APOLLO-01"
                  value={formData.contractNumber}
                  onChange={(e) => setFormData({ ...formData, contractNumber: e.target.value.toUpperCase() })}
                  disabled={isEditMode}
                />
                {errors.contractNumber && <span className="form-error">{errors.contractNumber}</span>}
              </div>

              <div className="form-group" style={{ flex: 2 }}>
                <label className="form-label required">Contract Title / Description</label>
                <input
                  type="text"
                  className={`form-input ${errors.contractName ? 'is-invalid' : ''}`}
                  placeholder="e.g. Corporate Employee Screening Tariff 2026"
                  value={formData.contractName}
                  onChange={(e) => setFormData({ ...formData, contractName: e.target.value })}
                />
                {errors.contractName && <span className="form-error">{errors.contractName}</span>}
              </div>

              <div className="form-group" style={{ flex: 2 }}>
                <label className="form-label required">B2B Client Party</label>
                <select
                  className={`form-input ${errors.partyId ? 'is-invalid' : ''}`}
                  value={formData.partyId}
                  onChange={(e) => setFormData({ ...formData, partyId: e.target.value })}
                  disabled={isEditMode}
                >
                  <option value="">-- Select B2B Party --</option>
                  {parties.map(p => (
                    <option key={p.partyId} value={p.partyId}>
                      {p.partyName} ({p.partyCode})
                    </option>
                  ))}
                </select>
                {errors.partyId && <span className="form-error">{errors.partyId}</span>}
                {partiesError && (
                  <span className="form-error" role="alert">
                    {partiesError} <button type="button" className="btn btn-secondary" style={{ padding: '2px 10px', marginLeft: 6 }} onClick={loadParties}>Try again</button>
                  </span>
                )}
                {!partiesError && parties.length === 0 && (
                  <span className="form-error">No active B2B client found. Create the client first in B2B Client / Party Master.</span>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label required">Valid From</label>
                <input
                  type="date"
                  className="form-input"
                  value={formData.validFrom}
                  onChange={(e) => setFormData({ ...formData, validFrom: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label className="form-label required">Valid To</label>
                <input
                  type="date"
                  className="form-input"
                  value={formData.validTo}
                  onChange={(e) => setFormData({ ...formData, validTo: e.target.value })}
                />
              </div>

              <div className="form-group" style={{ flex: 2 }}>
                <label className="form-label">Internal Remarks</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="e.g. Special executive health checkup discount terms"
                  value={formData.remarks}
                  onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                />
              </div>
            </div>
            {errors.dates && <div className="form-error" style={{ marginBottom: '1rem' }}>{errors.dates}</div>}
          </FormSection>

          <div className="form-actions" style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setViewMode('list')}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving}
            >
              {saving ? 'Saving...' : isEditMode ? 'Update Contract' : 'Create Contract'}
            </button>
          </div>
        </form>
      )}

      {/* Contract Items Modal / Tariff Sheet */}
      {activeContractForItems && (
        <div className="rc-tariff-modal-overlay" onClick={() => setActiveContractForItems(null)}>
          <div className="rc-tariff-modal" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="rc-tariff-header">
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', marginBottom: '0.35rem' }}>
                  <span className="rc-tariff-code-pill">{activeContractForItems.contractNumber}</span>
                  <h2 className="rc-tariff-title">
                    {activeContractForItems.partyName}
                  </h2>
                </div>
                <div className="rc-tariff-meta">
                  <span>Contract Tariff Sheet</span>
                  <span>•</span>
                  <span className="rc-tariff-validity-pill">
                    📅 Valid: {activeContractForItems.validFrom?.split('T')[0]} to {activeContractForItems.validTo?.split('T')[0]}
                  </span>
                  <span>•</span>
                  <span><strong>{contractItems.length}</strong> items configured</span>
                </div>
              </div>
              <button
                type="button"
                className="rc-tariff-close-btn"
                onClick={() => setActiveContractForItems(null)}
                title="Close Tariff Sheet"
              >
                ✕
              </button>
            </div>

            {/* Body */}
            <div className="rc-tariff-body">
              {/* Summary / KPI Bar */}
              <div className="rc-tariff-kpi-bar">
                <div className="rc-tariff-kpi-card">
                  <div className="rc-tariff-kpi-icon">📋</div>
                  <div>
                    <div className="rc-tariff-kpi-val">{tariffStats.totalItems}</div>
                    <div className="rc-tariff-kpi-label">Configured Tests</div>
                  </div>
                </div>

                <div className="rc-tariff-kpi-card">
                  <div className="rc-tariff-kpi-icon">🏷️</div>
                  <div>
                    <div className="rc-tariff-kpi-val" style={{ color: '#047857' }}>{tariffStats.avgDiscountPct}%</div>
                    <div className="rc-tariff-kpi-label">Average Discount</div>
                  </div>
                </div>

                <div className="rc-tariff-kpi-card">
                  <div className="rc-tariff-kpi-icon">💰</div>
                  <div>
                    <div className="rc-tariff-kpi-val" style={{ color: '#0284c7' }}>₹{tariffStats.totalSavings.toFixed(2)}</div>
                    <div className="rc-tariff-kpi-label">Total Client Margin</div>
                  </div>
                </div>
              </div>

              {/* Quick-Add / Override Item Form */}
              <div className="rc-tariff-form-card">
                <div className="rc-tariff-form-header">
                  <div className="rc-tariff-form-title">
                    <span>⚡</span> Add or Override Contracted Test Rate
                  </div>
                  {liveDiscount && (
                    <div className="rc-tariff-live-discount">
                      {liveDiscount.isDiscount ? (
                        <>✨ Client Saves: <strong>₹{liveDiscount.saving.toFixed(2)}</strong> ({liveDiscount.pct}% Off MRP)</>
                      ) : (
                        <>⚠️ Premium Surcharge: <strong>+₹{liveDiscount.saving.toFixed(2)}</strong> ({liveDiscount.pct}% Over MRP)</>
                      )}
                    </div>
                  )}
                </div>

                <form onSubmit={handleAddItem}>
                  <div className="rc-tariff-form-grid">
                    <div className="rc-tariff-field">
                      <label className="rc-tariff-field-label">Type</label>
                      <select
                        className="rc-tariff-select"
                        value={newItemType}
                        onChange={(e) => {
                          setNewItemType(e.target.value);
                          setNewItemId('');
                        }}
                      >
                        <option value="TEST">Investigation</option>
                        <option value="PROFILE">Profile</option>
                        <option value="PACKAGE">Package</option>
                      </select>
                    </div>

                    <div className="rc-tariff-field">
                      <label className="rc-tariff-field-label">Select Item</label>
                      <select
                        className="rc-tariff-select"
                        value={newItemId}
                        onChange={(e) => {
                          const selectedId = e.target.value;
                          setNewItemId(selectedId);
                          const found = selectableCatalog.find(item => String(item.id) === String(selectedId));
                          if (found && found.rate && !newMrp) {
                            setNewMrp(String(found.rate));
                            if (!newRate) setNewRate(String(found.rate));
                          }
                        }}
                      >
                        <option value="">-- Choose {newItemType.toLowerCase()} --</option>
                        {selectableCatalog.map(item => (
                          <option key={item.id} value={item.id}>
                            {item.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="rc-tariff-field">
                      <label className="rc-tariff-field-label">Contract Rate (₹)</label>
                      <input
                        type="number"
                        step="0.01"
                        className="rc-tariff-input highlight"
                        placeholder="e.g. 250"
                        value={newRate}
                        onChange={(e) => setNewRate(e.target.value)}
                      />
                    </div>

                    <div className="rc-tariff-field">
                      <label className="rc-tariff-field-label">MRP (₹)</label>
                      <input
                        type="number"
                        step="0.01"
                        className="rc-tariff-input"
                        placeholder="e.g. 400"
                        value={newMrp}
                        onChange={(e) => setNewMrp(e.target.value)}
                      />
                    </div>

                    <div>
                      <button
                        type="submit"
                        className="rc-tariff-add-btn"
                        disabled={addingItem || !newItemId || !newRate}
                      >
                        {addingItem ? 'Adding...' : '+ Add Rate'}
                      </button>
                    </div>
                  </div>
                </form>
              </div>

              {/* Items Table & Search Toolbar */}
              <div className="rc-tariff-table-toolbar">
                <div style={{ fontSize: '0.88rem', fontWeight: 700, color: '#334155' }}>
                  Contracted Rates ({filteredContractItems.length}{contractItems.length !== filteredContractItems.length ? ` of ${contractItems.length}` : ''})
                </div>
                {contractItems.length > 3 && (
                  <input
                    type="text"
                    className="rc-tariff-search-input"
                    placeholder="🔍 Search test, code, dept..."
                    value={tariffSearchQuery}
                    onChange={(e) => setTariffSearchQuery(e.target.value)}
                  />
                )}
              </div>

              {loadingItems ? (
                <div className="rc-tariff-empty">
                  <div className="rc-tariff-empty-icon">⏳</div>
                  <div>Loading contracted tariff rates...</div>
                </div>
              ) : contractItems.length === 0 ? (
                <div className="rc-tariff-empty">
                  <div className="rc-tariff-empty-icon">📑</div>
                  <div style={{ fontWeight: 600, color: '#334155', marginBottom: '0.25rem' }}>No Contract Rates Defined</div>
                  <div style={{ fontSize: '0.82rem' }}>
                    Standard counter MRP will be charged unless you add negotiated B2B rates for this party above.
                  </div>
                </div>
              ) : filteredContractItems.length === 0 ? (
                <div className="rc-tariff-empty">
                  <div className="rc-tariff-empty-icon">🔍</div>
                  <div style={{ fontWeight: 600, color: '#334155' }}>No tests match "{tariffSearchQuery}"</div>
                  <button
                    type="button"
                    style={{ marginTop: '0.5rem', background: 'none', border: 'none', color: '#0284c7', cursor: 'pointer', fontWeight: 600, fontSize: '0.82rem' }}
                    onClick={() => setTariffSearchQuery('')}
                  >
                    Clear search filter
                  </button>
                </div>
              ) : (
                <div className="rc-tariff-table-card">
                  <table className="rc-tariff-table">
                    <thead>
                      <tr>
                        <th style={{ width: '90px' }}>Type</th>
                        <th style={{ width: '100px' }}>Code</th>
                        <th>Item Name</th>
                        <th>Department</th>
                        <th style={{ textAlign: 'right', width: '130px' }}>Contract Rate</th>
                        <th style={{ textAlign: 'right', width: '130px' }}>MRP</th>
                        <th style={{ textAlign: 'center', width: '70px' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredContractItems.map((item) => {
                        const cRate = parseFloat(item.contractRate) || 0;
                        const mrp = parseFloat(item.mrpRate) || 0;
                        const diff = mrp > cRate ? mrp - cRate : 0;
                        const pct = mrp > 0 && diff > 0 ? ((diff / mrp) * 100).toFixed(0) : 0;

                        return (
                          <tr key={item.id}>
                            <td>
                              <span className={`rc-type-pill ${item.itemType}`}>
                                {item.itemType}
                              </span>
                            </td>
                            <td>
                              <code style={{ fontSize: '0.82rem', color: '#0369a1', background: '#f0f9ff', padding: '2px 5px', borderRadius: '4px' }}>
                                {item.itemCode || '—'}
                              </code>
                            </td>
                            <td>
                              <div style={{ fontWeight: 600, color: '#0f172a' }}>{item.itemName}</div>
                            </td>
                            <td style={{ color: '#64748b', fontSize: '0.82rem' }}>
                              {item.departmentName || '—'}
                            </td>
                            <td style={{ textAlign: 'right' }}>
                              <div className="rc-contract-rate">₹{cRate.toFixed(2)}</div>
                              {diff > 0 && (
                                <span className="rc-savings-pill">
                                  Save ₹{diff.toFixed(2)} ({pct}%)
                                </span>
                              )}
                            </td>
                            <td style={{ textAlign: 'right' }}>
                              <span className="rc-mrp-strike">₹{mrp.toFixed(2)}</span>
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <button
                                type="button"
                                className="rc-tariff-remove-btn"
                                onClick={() => handleRemoveItem(item.id)}
                                title="Remove Rate from Contract"
                              >
                                ✕
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="rc-tariff-footer">
              <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                💡 <strong>Tip:</strong> Contract rates automatically override standard catalog pricing whenever this party places an order or counter booking.
              </div>
              <button
                type="button"
                className="btn btn-secondary"
                style={{ padding: '0.45rem 1.25rem' }}
                onClick={() => setActiveContractForItems(null)}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Status Modal */}
      {confirmTarget && (
        <ConfirmModal
          isOpen={true}
          title={`${confirmTarget.nextStatus ? 'Activate' : 'Deactivate'} Contract`}
          message={`Are you sure you want to ${confirmTarget.nextStatus ? 'activate' : 'deactivate'} contract "${confirmTarget.contractName}"?`}
          confirmLabel={confirmTarget.nextStatus ? 'Activate' : 'Deactivate'}
          confirmVariant={confirmTarget.nextStatus ? 'primary' : 'danger'}
          onConfirm={confirmStatusChange}
          onCancel={() => setConfirmTarget(null)}
        />
      )}
    </MasterPageWrapper>
  );
};

export default RateContractPage;
